import { Link, useRouterState } from "@tanstack/react-router";
import classNames from "classnames";
import { useContext } from "react";
import { FaChevronRight, FaRightFromBracket } from "react-icons/fa6";

import UserContext from "@/contexts/user";

import {
  isNavPathActive,
  getVisibleNavItems,
  getVisibleNavSections,
} from "@/components/navConfig";

type MobileNavProps = {
  isOpen: boolean;
  onClose: () => void;
  onSignOut: () => void | Promise<void>;
};

export default function MobileNav({
  isOpen,
  onClose,
  onSignOut,
}: MobileNavProps) {
  const { user } = useContext(UserContext);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const visibleSections = getVisibleNavSections(user);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 top-16 z-40 overflow-y-auto bg-white lg:hidden">
      <nav className="flex flex-col divide-y divide-zinc-100">
        {visibleSections.map((section) => {
          const SectionIcon = section.icon;
          const sectionItems = getVisibleNavItems(section, user);

          return (
            <div key={section.key} className="py-3">
              <div className="flex items-center gap-2 px-5 pb-2 pt-1">
                {SectionIcon && (
                  <SectionIcon size={14} className="text-zinc-400" />
                )}
                <span className="text-xs font-semibold uppercase tracking-widest text-zinc-400">
                  {section.title}
                </span>
              </div>

              {sectionItems.map((item, index) => {
                const IconComponent = item.icon;
                const isActive =
                  item.to && !item.comingSoon
                    ? isNavPathActive(pathname, item.to)
                    : false;
                const previousItem = sectionItems[index - 1];
                const shouldRenderGroupLabel =
                  item.group && item.group !== previousItem?.group;

                const groupLabel = shouldRenderGroupLabel ? (
                  <p
                    className={classNames(
                      "px-6 pb-1 text-xs font-medium text-zinc-400",
                      index === 0 ? "pt-1" : "pt-3"
                    )}
                  >
                    {item.group}
                  </p>
                ) : null;

                if (item.comingSoon || !item.to) {
                  return (
                    <div key={item.key}>
                      {groupLabel}
                      <div
                        className="flex cursor-not-allowed items-center gap-3 px-6 py-3 text-sm text-zinc-300"
                        aria-disabled="true"
                      >
                        {IconComponent && (
                          <IconComponent size={15} className="shrink-0" />
                        )}
                        <span>{item.title}</span>
                        <span className="ml-auto rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-400">
                          Soon
                        </span>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={item.key}>
                    {groupLabel}
                    <Link
                      to={item.to}
                      onClick={onClose}
                      className={classNames(
                        "flex min-h-11 items-center gap-3 px-6 py-3 text-sm transition-[background-color,color,transform] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-100 active:scale-[0.96]",
                        isActive
                          ? "bg-blue-50 font-medium text-blue-500"
                          : "text-zinc-700 hover:bg-zinc-50"
                      )}
                    >
                      {IconComponent && (
                        <IconComponent
                          size={15}
                          className={classNames(
                            "shrink-0",
                            isActive ? "text-blue-400" : "text-zinc-400"
                          )}
                        />
                      )}
                      {item.title}
                      <FaChevronRight
                        size={10}
                        className="ml-auto text-zinc-300"
                      />
                    </Link>
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-zinc-100 px-5 py-4">
        <button
          type="button"
          className="flex min-h-10 w-full items-center gap-3 rounded-md text-sm text-zinc-600 transition-[background-color,color,transform] duration-150 ease-out hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100 active:scale-[0.96]"
          onClick={async () => {
            onClose();
            await onSignOut();
          }}
        >
          <FaRightFromBracket
            size={15}
            style={{ transform: "rotate(180deg)" }}
            className="text-zinc-400"
          />
          Sign Out
        </button>
      </div>
    </div>
  );
}
